import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import MuiDataGrid from 'components/MuiDataGrid';

import { FormProps, IPathology } from '../PatientList/api/type';
import PathologyDialog from './PathologyDialog';
import ListActionButtons from './ListActionButtons';
import { formatStringToDate } from 'utils/formatting';

interface Props extends FormProps {
  defaultList?: IPathology[];
}

const PathologyList = (props: Props) => {
  const { setValue, defaultList } = props;

  const { palette } = useTheme();

  const { list, setList } = useTable<IPathology>();
  const [defaultValue, setDefaultValue] = useState<Partial<IPathology> | null>(
    null
  );

  useEffect(() => setList(defaultList ?? []), [defaultList, setList]);
  useEffect(() => {
    setValue(
      'pathology',
      // Store object in API update format
      list.map(item => ({
        ...item,
        pathologyTime: formatStringToDate(item.pathologyTime),
      }))
    );
  }, [list, setValue]);

  const onCreateStart = () =>
    setDefaultValue({ pathologyTime: new Date().toString() });
  const onCreateList = (data: IPathology) => setList(list.concat(data));
  const onUpdateList = (data: IPathology) =>
    setList(
      list.map(item =>
        item.ptPathologyNo === data.ptPathologyNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IPathology }) => {
    const onUpdate = () => setDefaultValue(row);
    const onDelete = () =>
      setList(list.filter(item => item.ptPathologyNo !== row.ptPathologyNo));

    return <ListActionButtons onUpdate={onUpdate} onDelete={onDelete} />;
  };

  const columns: GridColDef[] = [
    { field: 'ptPathologyNo', headerName: 'Шинжилгээний Дугаар' },
    {
      field: 'pathologyTime',
      headerName: 'Шинжилгээний Цаг',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    { field: 'feeId', headerName: 'Шинжилгээний Код' },
    { field: 'feeKor', headerName: 'Шинжилгээний Нэр (Солонгос)' },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: ({ row }) => <ActionButtons row={row} />,
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" sx={{ color: `${palette.primary.main}` }}>
            Эмгэг Судлалын Шинжилгээ (Засварлах боломжтой)
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            (Нэмэхийн тулд SmartNurse ENR-тэй холбогдоно уу.)
          </Typography>

          {/* <Button
            size="small"
            variant="outlined"
            onClick={onCreateStart}
            sx={{ ml: 3 }}
          >
            Нэмэх
          </Button> */}
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptPathologyNo }))}
        />
      </Stack>

      {defaultValue && (
        <PathologyDialog
          isUpdate={defaultValue.ptPathologyNo ? true : false}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptPathologyNo === no) ? true : false
          }
          onSave={defaultValue.ptPathologyNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default PathologyList;
